/**
 * dscSlotConfig.js
 *
 * Declarative configuration for each DSC slot card (per product line) +
 * canonical tab orders + per-tab facet config + per-tab product-source config.
 *
 * @module data/dscSlotConfig
 * @since 2026-05-20 (Group E SP1.7)
 */

// ============================================================================
// Slot configuration — one entry per slot, per productLine
// ============================================================================

export const DSC_SLOT_CONFIG = {
  TH: {
    'shank-adapter': {
      id: 'shank-adapter',
      title: 'TH Shanks',
      image: '/assets/dsc/THShankAdapterImage.png',
      dropdownLabel: 'Which rock drill are you using?',
      filledChipFormat: (sel) =>
        sel && sel.mfr && sel.model ? `${sel.mfr} ${sel.model}` : (sel?.mfr || sel?.model || ''),
      pgmj: 184,
      pgmn: 'T20',
    },
    couplings: {
      id: 'couplings',
      title: 'Couplings',
      image: '/assets/dsc/CouplingImage.jpeg',
      dropdownLabel: 'Which type of coupling sleeve?',
      filledChipFormat: (sel) => {
        if (!sel) return '';
        const typeLabel =
          sel.type === 'Reduction Coupling' ? 'Reduction sleeve' :
          sel.type === 'Normal' ? 'Coupling sleeve' :
          sel.type || '';
        return sel.rodThread ? `${typeLabel} · ${sel.rodThread}` : typeLabel;
      },
      pgmj: 184,
      pgmn: 'T24',
    },
    rod: {
      id: 'rod',
      title: 'TH Rods',
      image: '/assets/dsc/THRodImage.png',
      imageWithCoupling: '/assets/dsc/THRodImage_Coupling.png',
      dropdownLabel: 'Which thread does your bit support?',
      filledChipFormat: (sel) => sel?.thread || '',
      pgmj: 184,
      pgmn: 'T28',
    },
    'bit-adapter': {
      id: 'bit-adapter',
      title: 'Bit Adapters',
      image: '/assets/dsc/THRodImage.png',
      dropdownLabel: 'Which adapter thread?',
      filledChipFormat: (sel) => sel?.thread || '',
      pgmj: null,
      pgmn: null,
    },
    'drill-bit': {
      id: 'drill-bit',
      title: 'TH Bits',
      image: '/assets/dsc/THBitImage.png',
      dropdownLabel: 'Which type of bit?',
      filledChipFormat: (sel) => sel?.type || sel?.buttonShape || '',
      pgmj: 182,
      pgmn: null,
    },
  },
  DTH: {
    'dth-bit': {
      id: 'dth-bit',
      title: 'DTH Bit',
      image: '/assets/dsc/DTHBitImage.png',
      imageLight: '/assets/dsc/DTHBitImage_lightMode.png',
      dropdownLabel: 'What hole size are you looking to drill?',
      filledChipFormat: (sel) => sel?.holeSize || sel?.bitDiameter || '',
      pgmj: 832,
      pgmn: null,
    },
    'hammer-series': {
      id: 'hammer-series',
      title: 'DTH Hammer',
      image: '/assets/dsc/DTHHammerImage.png',
      imageLight: '/assets/dsc/DTHHammerImage_lightMode.png',
      dropdownLabel: 'Which Hammer Series do you have?',
      secondaryDropdownLabel: 'What shank type for the Bit?',
      filledChipFormat: (sel) => {
        if (!sel) return '';
        if (sel.family && sel.size) return `${sel.family}-${sel.size}`;
        return sel.family || sel.shank || '';
      },
      pgmj: 830,
      pgmn: null,
    },
    drillpipe: {
      id: 'drillpipe',
      title: 'DTH Drillpipe',
      image: '/assets/dsc/DTHDrillpipe.png',
      imageLight: '/assets/dsc/DTHDrillpipe_lightMode.png',
      dropdownLabel: 'Which Pin option?',
      filledChipFormat: (sel) => sel?.pinOption || '',
      pgmj: 849,
      pgmn: null,
    },
  },
};

// ============================================================================
// Tab order — CANONICAL (plan §1.3)
// ============================================================================

export const TH_TAB_ORDER = [
  'shank-adapters',
  'coupling-sleeve',
  'rod-extensions',
  'bit-adapters',
  'th-bits',
];

export const DTH_TAB_ORDER = [
  'dth-hammers',
  'dth-drill-pipes',
  'dth-bits',
];

// ============================================================================
// Per-tab facet configuration
// ============================================================================

export const DSC_TAB_FACETS = {
  'shank-adapters':  ['diameterMm', 'lengthMm', 'compatibleRockDrill', 'thread'],
  'coupling-sleeve': ['couplingType', 'diameterMm', 'threadF2', 'threadF1'],
  'rod-extensions':  ['diameterMm', 'lengthMm', 'rodType', 'thread2'],
  'bit-adapters':    ['thread', 'diameterMm', 'lengthMm', 'pilotBitThread'],
  'th-bits':         ['bitDiameterMm', 'buttonShape', 'carbideGrade', 'thread'],
  'dth-hammers':     ['hammerFamily', 'hammerSize_in', 'shankType', 'hammerOutsideDiameter_mm'],
  'dth-drill-pipes': ['topsubPinOption', 'diameter_mm', 'length_mm'],
  'dth-bits':        ['bitHeadDiameter_mm', 'shankType', 'bitFaceDesign', 'carbideShape'],
};

// ============================================================================
// Per-tab facet → cascade slot mapping (task #15 — bidirectional sync)
// ============================================================================

/**
 * When a facet chip in the DSC results tab is clicked, back-propagate the
 * selection to the corresponding cascade slot via SET_SELECTION. Only clean
 * 1:1 mappings are included; ambiguous / composite / display-only facets
 * (diameter, length, hammerFamily-without-size, etc.) are left as pure
 * display filters. See task #15 in STATUS-2026-05-20-EOD.md.
 *
 * Shape: `{ [tabSlug]: { [facetId]: { slotId, dropdownKey? } } }`.
 *   - `dropdownKey` is omitted for slots whose reducer stores value under a
 *     fixed key (rod → thread, drillpipe → pinOption).
 *   - Multi-select (2+ active values) or empty facets skip the dispatch —
 *     cascade slots are single-select and multi-select doesn't map cleanly.
 */
export const DSC_TAB_FACET_TO_CASCADE = {
  'coupling-sleeve': {
    couplingType: { slotId: 'couplings', dropdownKey: 'couplingType' },
    threadF2:     { slotId: 'couplings', dropdownKey: 'threadForRod' },
  },
  'rod-extensions': {
    thread2:      { slotId: 'rod' }, // reduceRodSlot stores as {thread: value}
  },
  'bit-adapters': {
    thread:       { slotId: 'bit-adapter', dropdownKey: 'thread' },
  },
  'th-bits': {
    buttonShape:  { slotId: 'drill-bit', dropdownKey: 'type' },
  },
  'dth-hammers': {
    shankType:    { slotId: 'hammer-series', dropdownKey: 'shank' },
  },
  'dth-drill-pipes': {
    topsubPinOption: { slotId: 'drillpipe' }, // reducer stores as {pinOption: value}
  },
};

/**
 * Pure decision helper for task #15 back-propagation. Given the current
 * activeFilters map, the facet being toggled, and the value being clicked,
 * returns either `null` (no cascade dispatch) or the SET_SELECTION args
 * `{ slotId, dropdownKey, value }` the caller should dispatch.
 *
 * Rules:
 *   - If no mapping for (tabSlug, facetId) → null.
 *   - If the toggle empties the facet → { …, value: 'None' } (clears the
 *     cascade slot via the reducer's None-branch).
 *   - If the toggle results in exactly one value → dispatch that value.
 *   - If the toggle results in 2+ values (multi-select) → null (cascade
 *     slots are single-select).
 *
 * Extracted so DSCResultsTabPanel's handler is a thin wrapper around this
 * pure function.
 */
export function resolveFacetCascadeDispatch(activeFilters, tabSlug, facetId, value) {
  const mapping = DSC_TAB_FACET_TO_CASCADE[tabSlug]?.[facetId];
  if (!mapping) return null;
  const current = activeFilters?.[facetId] || [];
  const isRemoving = current.includes(value);
  const nextValues = isRemoving
    ? current.filter((v) => v !== value)
    : [...current, value];
  if (nextValues.length === 0) {
    return { slotId: mapping.slotId, dropdownKey: mapping.dropdownKey, value: 'None' };
  }
  if (nextValues.length === 1) {
    return { slotId: mapping.slotId, dropdownKey: mapping.dropdownKey, value: nextValues[0] };
  }
  return null; // multi-select, no dispatch
}

// ============================================================================
// Per-tab product config — which product groups feed each tab
// ============================================================================

/**
 * Rewritten 20 May 2026 to use subcategory (TH) / category (DTH) instead of
 * legacy (pgmj, pgmn) tuples — PWA data uses different itemGroupMinor codes
 * than the legacy Apex schema.
 *
 * Tab → product mapping (data audit 20 May 2026):
 *   shank-adapters    → ['shank-adapters']  (342)
 *   coupling-sleeve   → ['coupling-sleeves']  (35)
 *   rod-extensions    → ['drifter-rods','mf-guide-rods','extension-rods','shank-rods','drill-tubes','guide-tubes']  (365)
 *   bit-adapters      → ['pilot-bit-adapters']  (49; further narrowed by description in dscTabFilter)
 *   th-bits           → ['drill-bits','reamer-bits']  (481)
 *   dth-hammers       → category ['dth-hammers']  (623; RH560/RH570 only)
 *   dth-drill-pipes   → category ['dth-drill-pipes']  (179)
 *   dth-bits          → category ['dth-bits']  (618)
 */
export const DSC_TAB_PRODUCT_CONFIG = {
  'shank-adapters':  { productLine: 'TH',  subcategories: ['shank-adapters'] },
  'coupling-sleeve': { productLine: 'TH',  subcategories: ['coupling-sleeves'] },
  'rod-extensions':  {
    productLine: 'TH',
    subcategories: [
      'drifter-rods',
      'mf-guide-rods',
      'extension-rods',
      'shank-rods',
      'drill-tubes',
      'guide-tubes',
    ],
  },
  'bit-adapters':    { productLine: 'TH',  subcategories: ['pilot-bit-adapters'] },
  'th-bits':         { productLine: 'TH',  subcategories: ['drill-bits', 'reamer-bits'] },
  'dth-hammers':     { productLine: 'DTH', categories: ['dth-hammers'] },
  'dth-drill-pipes': { productLine: 'DTH', categories: ['dth-drill-pipes'] },
  'dth-bits':        { productLine: 'DTH', categories: ['dth-bits'] },
};

// ============================================================================
// Helpers
// ============================================================================

export function getSlotOrder(productLine, toggles = {}) {
  if (productLine === 'TH') {
    const ids = ['shank-adapter'];
    if (toggles.coupling) ids.push('couplings');
    ids.push('rod');
    if (toggles.bitAdapter) ids.push('bit-adapter');
    ids.push('drill-bit');
    return ids;
  }
  if (productLine === 'DTH') {
    return ['dth-bit', 'hammer-series', 'drillpipe'];
  }
  return [];
}

export function getTabOrder(productLine, toggles = {}) {
  if (productLine === 'TH') {
    return TH_TAB_ORDER.filter((tabId) => {
      if (tabId === 'coupling-sleeve' && !toggles.coupling) return false;
      if (tabId === 'bit-adapters' && !toggles.bitAdapter) return false;
      return true;
    });
  }
  if (productLine === 'DTH') return [...DTH_TAB_ORDER];
  return [];
}

/**
 * Hammer-family display rank — RH570 surfaces above RH560, then other RHxxx ascending.
 */
export function hammerFamilyRank(family) {
  if (!family) return 9999;
  const f = String(family).toUpperCase();
  if (f === 'RH570') return 0;
  if (f === 'RH560') return 1;
  const match = f.match(/^RH(\d+)/);
  if (match) return 100 + parseInt(match[1], 10);
  return 9000;
}
